import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button.js';
import { cn } from '../lib/utils.js';

/**
 * Numbered pagination bar (Previous | 1 … 4 5 6 … 30 | Next) for lists that
 * page through a fixed-size window instead of an unbounded infinite-scroll
 * reveal. Truncates the page-number list around the current page, always
 * keeping the first and last page visible.
 */

export interface PaginationLabels {
  previous: string;
  next: string;
  /** Accessible name for a page-number button, e.g. `(page) => \`Strona ${page}\`` */
  pageAriaLabel: (page: number) => string;
}

/** Page numbers to render, with `'ellipsis'` markers where a run is collapsed. */
export function getPaginationRange(current: number, pageCount: number): (number | 'ellipsis')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [1];
  if (current > 3) pages.push('ellipsis');

  const start = Math.max(2, current - 1);
  const end = Math.min(pageCount - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < pageCount - 2) pages.push('ellipsis');
  pages.push(pageCount);
  return pages;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  labels,
  className,
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  labels: PaginationLabels;
  className?: string;
}) {
  if (pageCount <= 1) return null;

  const range = getPaginationRange(page, pageCount);

  return (
    <nav
      className={cn('flex items-center justify-center gap-1', className)}
      data-testid="pagination"
    >
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label={labels.previous}
        title={labels.previous}
        data-testid="pagination-previous"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>

      {range.map((entry, i) =>
        entry === 'ellipsis' ? (
          // At most one leading and one trailing ellipsis ever appear (see
          // `getPaginationRange`), so the neighbouring page number is a
          // stable key without falling back to the array index.
          <span
            key={`ellipsis-before-${range[i + 1]}`}
            className="px-1.5 text-xs text-muted-foreground"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <Button
            key={entry}
            type="button"
            variant={entry === page ? 'default' : 'outline'}
            size="icon-sm"
            onClick={() => onPageChange(entry)}
            aria-label={labels.pageAriaLabel(entry)}
            aria-current={entry === page ? 'page' : undefined}
            data-testid={`pagination-page-${entry}`}
            className="text-xs"
          >
            {entry}
          </Button>
        ),
      )}

      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= pageCount}
        aria-label={labels.next}
        title={labels.next}
        data-testid="pagination-next"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </nav>
  );
}
